<template>
  <div class="time-picker-component">
    <!-- Time Input Field -->
    <div class="floating-input-container" :class="{ 'disabled': disabled }">
      <input 
        :value="inputValue"
        class="time-input"
        :class="{ 'has-value': inputValue || isFocused, 'error': validationError, 'disabled': disabled }"
        placeholder=" "
        :id="fieldId"
        autocomplete="off"
        :disabled="disabled"
        @click="focusInput"
        @input="handleManualInput"
        @blur="handleBlur"
        @keydown="handleKeydown"
        @focus="handleFocus"
        ref="timeInput"
        maxlength="8"
      />
      <label :for="fieldId" class="floating-label" :class="{ 'float': inputValue || isFocused, 'disabled': disabled }">
        {{ label }}<span v-if="required">*</span>
      </label>
      <IconProvider name="Clock" class="clock-icon" :class="{ 'disabled': disabled }" @click.stop="disabled ? null : toggleTimePicker()" />
      
      <!-- Error message positioned absolutely -->
      <div v-if="validationError" class="error-message">
        {{ validationError }}
      </div>
    </div>

    <!-- Circular Time Picker Modal -->
    <v-dialog v-model="showTimePicker" max-width="360" persistent>
      <v-card class="circular-time-picker">
        <!-- Header with selected time - clickable segments -->
        <div class="time-header">
          <div class="time-display">
            <span 
              class="time-segment hour-segment" 
              :class="{ active: selectionMode === 'hour' }"
              @click="setSelectionMode('hour')"
            >
              {{ formattedHour }}
            </span>
            <span class="time-separator">:</span>
            <span 
              class="time-segment minute-segment" 
              :class="{ active: selectionMode === 'minute' }"
              @click="setSelectionMode('minute')"
            >
              {{ formattedMinute }}
            </span>
            <span 
            class="ampm-text clickable-ampm" 
          >
            {{ selectedPeriod }}
          </span>
          </div>
        </div>
        
        <!-- Circular Clock -->
        <div class="clock-container">
          <div class="clock-face" @click="handleClockClick">
            <!-- Circle positioning container -->
            <div class="clock-numbers-circle"></div>
            
            <!-- Hour numbers (shown when selectionMode is 'hour') -->
            <div 
              v-if="selectionMode === 'hour'"
              v-for="hour in 12" 
              :key="'hour-' + hour"
              class="clock-number hour-number"
              :style="getHourNumberPosition(hour)"
              @click.stop="selectHour(hour)"
              :class="{ active: selectedHour === hour }"
            >
              {{ hour.toString() }}
            </div>
            
            <!-- Minute numbers (shown when selectionMode is 'minute') -->
            <div 
              v-if="selectionMode === 'minute'"
              v-for="minute in minuteMarks" 
              :key="'minute-' + minute"
              class="clock-number minute-number"
              :style="getMinuteNumberPosition(minute)"
              @click.stop="selectMinute(minute)"
              :class="{ active: selectedMinute === parseInt(minute) }"
            >
              {{ minute }}
            </div>
            
            <!-- Clock hands -->
            <div class="clock-center"></div>
            <div 
              v-if="selectionMode === 'hour'"
              class="hour-hand" 
              :class="{ 'hide-pointer': isHourOnNumber }"
              :style="getHourHandStyle()"
            ></div>
            <div 
              v-if="selectionMode === 'minute'"
              class="minute-hand" 
              :class="{ 'hide-pointer': isMinuteOnNumber }"
              :style="getMinuteHandStyle()"
            ></div>
          </div>
          
          <!-- AM/PM selectors -->
          <div class="ampm-selector">
            <button 
              class="am-btn"
              :class="{ active: selectedPeriod === 'AM' }"
              @click="selectedPeriod = 'AM'"
            >
              AM
            </button>
            <button 
              class="pm-btn"
              :class="{ active: selectedPeriod === 'PM' }"
              @click="selectedPeriod = 'PM'"
            >
              PM
            </button>
          </div>
        </div>
        
        <!-- Action buttons -->
        <div class="action-buttons">
          <v-btn
            variant="text"
            color="#003F2D"
            @click="clearTime"
          >
            Clear
          </v-btn>
          <v-btn
            variant="flat"
            color="#003F2D"
            @click="confirmTime"
          >
            Ok
          </v-btn>
        </div>
      </v-card>
    </v-dialog>
  </div>
</template>

<script>
import IconProvider from './IconProvider.vue';

export default {
  name: 'TimePickerComponent',
  components: {
    IconProvider
  },
  props: {
    modelValue: {
      type: String,
      default: ''
    },
    label: {
      type: String,
      default: 'Time'
    },
    required: {
      type: Boolean,
      default: false
    },
    disabled: {
      type: Boolean,
      default: false
    }
  },
  emits: ['update:modelValue', 'input', 'validation-change'],
  data() {
    return {
      showTimePicker: false,
      selectedHour: 12,
      selectedMinute: 0,
      selectedPeriod: 'PM',
      selectionMode: 'hour',
      validationError: '',
      isManualInput: false,
      inputValue: '',
      isFocused: false,
      lastValidValue: '' // Track the last valid value
    }
  },
  computed: {
    fieldId() {
      return 'time-' + this._uid || Math.random().toString(36).substr(2, 9);
    },
    minuteMarks() {
      return ['00', '05', '10', '15', '20', '25', '30', '35', '40', '45', '50', '55'];
    },
    displayTime() {
      if (!this.modelValue || this.modelValue === '') {
        return '';
      }
      const timeMatch = this.modelValue.match(/(\d{1,2}):(\d{2})\s*(AM|PM)?/i);
      if (timeMatch) {
        const hour = timeMatch[1].padStart(2, '0');
        const minute = timeMatch[2];
        const period = timeMatch[3] || this.selectedPeriod;
        return `${hour}:${minute} ${period}`;
      }
      return this.modelValue;
    },
    formattedTime() {
      const hour = this.selectedHour.toString().padStart(2, '0');
      const minute = this.selectedMinute.toString().padStart(2, '0');
      return `${hour}:${minute} ${this.selectedPeriod}`;
    },
    formattedHour() {
      return this.selectedHour.toString();
    },
    formattedMinute() {
      return this.selectedMinute.toString().padStart(2, '0');
    },
    isHourOnNumber() {
      // Always hide pointer for hour hand when it's pointing at or near hour numbers
      // The hour hand should appear clean without the circular pointer head
      return true;
    },
    isMinuteOnNumber() {
      // Check if minute hand is pointing directly at a 5-minute mark
      return this.selectedMinute % 5 === 0;
    },
    isValid() {
      return !this.validationError || this.validationError.trim() === '';
    }
  },
  methods: {
    handleFocus(event) {
      this.isFocused = true;
      // When focusing, allow editing by setting cursor at end
      this.$nextTick(() => {
        const input = event.target;
        const length = input.value.length;
        input.setSelectionRange(length, length);
      });
    },

    handleBlur(event) {
      this.isFocused = false;
      this.validateAndFormatInput(event);
    },

    focusInput() {
      // Allow normal text input behavior
      this.$refs.timeInput.focus();
    },
    
    handleManualInput(event) {
      this.isManualInput = true;
      // Clear error immediately when user starts typing
      if (this.validationError) {
        this.validationError = '';
        this.emitValidationState();
      }
      const value = event.target.value;
      
      // Update the input value immediately to allow typing
      this.inputValue = value;
      
      // Allow partial typing
      if (this.isPartialTimeValid(value)) {
        // Don't emit yet, wait for complete input
      }
    },
    
    handleKeydown(event) {
      // Allow backspace, delete, tab, escape, enter, and arrow keys
      if ([8, 9, 27, 13, 37, 38, 39, 40, 46].indexOf(event.keyCode) !== -1 ||
          // Allow Ctrl+A, Ctrl+C, Ctrl+V, Ctrl+X
          (event.keyCode === 65 && event.ctrlKey === true) ||
          (event.keyCode === 67 && event.ctrlKey === true) ||
          (event.keyCode === 86 && event.ctrlKey === true) ||
          (event.keyCode === 88 && event.ctrlKey === true)) {
        return;
      }
      
      // Allow numbers, colon, space, A, M, P
      const allowedChars = /[0-9:\sAMPamp]/;
      if (!allowedChars.test(event.key)) {
        event.preventDefault();
      }
    },
    
    isPartialTimeValid(value) {
      // Allow empty input
      if (value === '') return true;
      
      // Allow partial time formats during typing
      const partialPatterns = [
        /^\d{1,2}$/, // Just hour
        /^\d{1,2}:$/, // Hour with colon
        /^\d{1,2}:\d{1,2}$/, // Hour and minute
        /^\d{1,2}:\d{2}\s*$/, // Hour, minute, and space
        /^\d{1,2}:\d{2}\s*[APap]?$/, // With partial AM/PM
        /^\d{1,2}:\d{2}\s*[APap][Mm]?$/  // Complete or partial AM/PM
      ];
      
      return partialPatterns.some(pattern => pattern.test(value));
    },
    
    validateAndFormatInput(event) {
      const value = event.target.value.trim();
      
      if (value === '') {
        this.clearTime();
        return;
      }
      
      const validationResult = this.validateTimeInput(value);
      
      if (validationResult.isValid) {
        this.validationError = '';
        const { hour, minute, period } = validationResult;
        
        // Update internal state
        this.selectedHour = hour;
        this.selectedMinute = minute;
        this.selectedPeriod = period;
        
        // Format with leading zero and emit the validated time (HH:MM AM/PM format)
        const formattedTime = `${hour.toString().padStart(2, '0')}:${minute.toString().padStart(2, '0')} ${period}`;
        this.inputValue = formattedTime;
        this.lastValidValue = formattedTime; // Store the last valid value
        this.$emit('update:modelValue', formattedTime);
        this.$emit('input', formattedTime);
      } else {
        // Keep the invalid text in the input field
        this.validationError = validationResult.error;
        // Don't change the input value - let user see their invalid input
        // Don't emit model update for invalid values
      }
      
      // Emit validation state change
      this.emitValidationState();
      this.isManualInput = false;
    },
    
    validateTimeInput(value) {
      // Match time format: H:MM AM/PM or HH:MM AM/PM
      const timeRegex = /^(\d{1,2}):(\d{1,2})\s*(AM|PM|am|pm)$/i;
      const match = value.match(timeRegex);
      
      if (!match) {
        return {
          isValid: false,
          error: 'Please enter time in format: HH:MM AM/PM (e.g., 02:30 PM)'
        };
      }
      
      const [, hourStr, minuteStr, periodStr] = match;
      const hour = parseInt(hourStr);
      const minute = parseInt(minuteStr);
      const period = periodStr.toUpperCase();
      
      // Validate hour (1-12)
      if (hour < 1 || hour > 12) {
        return {
          isValid: false,
          error: 'Hour must be between 01 and 12'
        };
      }
      
      // Validate minute (0-59)
      if (minute < 0 || minute > 59) {
        return {
          isValid: false,
          error: 'Minutes must be between 00 and 59'
        };
      }
      
      // Validate AM/PM
      if (period !== 'AM' && period !== 'PM') {
        return {
          isValid: false,
          error: 'Please specify AM or PM'
        };
      }
      
      return {
        isValid: true,
        hour,
        minute,
        period
      };
    },
    
    toggleTimePicker() {
      this.showTimePicker = !this.showTimePicker;
      if (this.showTimePicker) {
        this.selectionMode = 'hour';
        this.parseCurrentTime();
      }
    },
    
    parseCurrentTime() {
      if (this.modelValue) {
        const timeMatch = this.modelValue.match(/(\d{1,2}):(\d{2})\s*(AM|PM)?/i);
        if (timeMatch) {
          const hour = parseInt(timeMatch[1]);
          const minute = parseInt(timeMatch[2]);
          const ampm = timeMatch[3] || 'AM';
          
          this.selectedHour = hour === 0 ? 12 : hour > 12 ? hour - 12 : hour;
          this.selectedMinute = minute;
          this.selectedPeriod = ampm.toUpperCase();
        }
      } else {
        const now = new Date();
        let hour = now.getHours();
        const minute = now.getMinutes();
        const period = hour >= 12 ? 'PM' : 'AM';
        
        if (hour === 0) hour = 12;
        else if (hour > 12) hour -= 12;
        
        this.selectedHour = hour;
        this.selectedMinute = minute;
        this.selectedPeriod = period;
      }
    },
    selectHour(hour) {
      this.selectedHour = hour;
      this.selectionMode = 'minute';
    },
    selectMinute(minute) {
      this.selectedMinute = parseInt(minute);
    },
    setSelectionMode(mode) {
      this.selectionMode = mode;
    },
    togglePeriod() {
      this.selectedPeriod = this.selectedPeriod === 'AM' ? 'PM' : 'AM';
    },
    getHourNumberPosition(hour) {
      const angle = (hour * 30) * (Math.PI / 180);
      const radius = 95; // Increased radius for better spacing
      const x = Math.sin(angle) * radius;
      const y = -Math.cos(angle) * radius;
      
      return {
        transform: `translate(${x}px, ${y}px)`,
        position: 'absolute',
        top: '50%',
        left: '50%'
      };
    },
    getMinuteNumberPosition(minute) {
      const minuteValue = parseInt(minute);
      const angle = (minuteValue * 6) * (Math.PI / 180);
      const radius = 95; // Increased radius for better spacing
      const x = Math.sin(angle) * radius;
      const y = -Math.cos(angle) * radius;
      
      return {
        transform: `translate(${x}px, ${y}px)`,
        position: 'absolute',
        top: '50%',
        left: '50%'
      };
    },
    getHourHandStyle() {
      let displayHour = this.selectedHour;
      if (displayHour === 12) displayHour = 0;
      
      // When in hour selection mode, point directly at the selected hour
      // When in minute selection mode, include minute offset for realistic clock behavior
      const minuteOffset = this.selectionMode === 'hour' ? 0 : (this.selectedMinute * 0.5);
      const angle = displayHour * 30 + minuteOffset;
      return {
        transform: `rotate(${angle}deg)`,
        transformOrigin: 'bottom center'
      };
    },
    getMinuteHandStyle() {
      const angle = this.selectedMinute * 6;
      return {
        transform: `rotate(${angle}deg)`,
        transformOrigin: 'bottom center'
      };
    },
    handleClockClick(event) {
      const rect = event.currentTarget.getBoundingClientRect();
      const centerX = rect.left + rect.width / 2;
      const centerY = rect.top + rect.height / 2;
      const x = event.clientX - centerX;
      const y = event.clientY - centerY;
      
      let angle = Math.atan2(x, -y) * (180 / Math.PI);
      if (angle < 0) angle += 360;
      
      if (this.selectionMode === 'minute') {
        const minute = Math.round(angle / 6) % 60;
        this.selectedMinute = minute;
      } else if (this.selectionMode === 'hour') {
        let hour = Math.round(angle / 30);
        if (hour === 0 || hour === 12) hour = 12;
        else if (hour > 12) hour = hour - 12;
        this.selectedHour = hour;
        this.selectionMode = 'minute';
      }
    },
    confirmTime() {
      const hour = this.selectedHour.toString().padStart(2, '0');
      const minute = this.selectedMinute.toString().padStart(2, '0');
      const displayTime = `${hour}:${minute} ${this.selectedPeriod}`;
      
      this.inputValue = displayTime;
      this.lastValidValue = displayTime;
      this.$emit('update:modelValue', displayTime);
      this.$emit('input', displayTime);
      this.showTimePicker = false;
      this.validationError = '';
      this.emitValidationState();
    },
    
    clearTime() {
      this.selectedHour = 12;
      this.selectedMinute = 0;
      this.selectedPeriod = 'AM';
      this.selectionMode = 'hour';
      this.validationError = '';
      this.inputValue = '';
      this.lastValidValue = '';
      const defaultTime = '';
      this.$emit('update:modelValue', defaultTime);
      this.$emit('input', defaultTime);
      this.showTimePicker = false;
      this.emitValidationState();
    },

    emitValidationState() {
      this.$emit('validation-change', {
        isValid: this.isValid,
        error: this.validationError
      });
    }
  },
  
  mounted() {
    this.inputValue = this.displayTime;
    this.lastValidValue = this.displayTime;
    if (!this.modelValue) {
      this.parseCurrentTime();
    }
  },
  watch: {
    modelValue: {
      handler(newVal) {
        if (!this.isManualInput) {
          this.inputValue = this.displayTime;
          this.lastValidValue = this.displayTime;
          if (newVal) {
            this.parseCurrentTime();
          }
        }
      },
      immediate: true
    },
    
    validationError: {
      handler() {
        this.emitValidationState();
      }
    }
  }
}
</script>

<style scoped>
.time-picker-component {
  position: relative;
  width: 100%;
  height: 48px; /* Fixed height matching input */
  margin-bottom: 16px; /* Space for potential error message */
}

.floating-input-container {
  position: relative;
  width: 100%;
  height: 48px; /* Fixed height matching input */
}

.time-input {
  width: 100%;
  padding: 8px;
  border: 1px solid #5A5A5A;
  border-radius: 4px;
  font-size: 14px;
  background: transparent;
  transition: border-color 0.3s ease;
  box-sizing: border-box;
  cursor: text;
  height: 48px;
}

.time-input.error {
  border-color: #f44336 !important;
}

.time-input:focus {
  outline: none;
  border-color: #5A5A5A;
}

.time-input.error:focus {
  border-color: #f44336 !important;
}

.floating-label {
  position: absolute;
  left: 8px;
  top: 16px;
  font-size: 14px;
  color: #000 !important;
  transition: all 0.3s ease;
  pointer-events: none;
  background: white;
  padding: 0 4px;
  font-family: 'Segoe UI, -apple-system, BlinkMacSystemFont, system-ui, Roboto, Helvetica Neue, sans-serif' !important;
}

.floating-label.float {
  top: -8px;
  font-size: 14px;
  color: #5A5A5A;
}

.clock-icon {
  position: absolute;
  right: 12px;
  top: 50%;
  transform: translateY(-50%);
  cursor: pointer;
  color: #5A5A5A;
  width: 20px;
  height: 20px;
}

.clock-icon.disabled {
  cursor: not-allowed;
  color: #bbb;
  opacity: 0.5;
}

.time-input.disabled {
  background-color: #f5f5f5;
  cursor: not-allowed;
  color: #999;
}

.floating-label.disabled {
  color: #999;
}

.floating-input-container.disabled {
  pointer-events: none;
  opacity: 0.6;
}

.error-message {
  position: absolute;
  top: 100%;
  left: 0;
  right: 0;
  color: #f44336;
  font-size: 12px;
  margin-top: 4px;
  font-family: "Calibre", sans-serif !important;
  line-height: 1.2;
  z-index: 10;
  background: white;
}

/* Circular Time Picker Styles */
.circular-time-picker {
  background: #fff;
  border-radius: 8px;
  overflow: hidden;
}

.time-header {
  background: #003F2D;
  color: white;
  text-align: center;
  padding: 20px;
  display: flex;
  justify-content: space-between;
  align-items: baseline;
  position: relative;
}

.time-display {
  color: #FFFFFF;
  display: -webkit-box;
  display: flex;
  line-height: 1;
  -webkit-box-pack: end;
  -ms-flex-pack: end;
  justify-content: flex-end;
  align-items: baseline;
  font-size: 32px;
  font-weight: 300;
  flex: 1;
  justify-content: right;
  font-family: "Calibre", sans-serif !important;
}

.time-segment {
  cursor: pointer;
  border-radius: 4px;
  transition: all 0.2s ease;
  user-select: none;
  text-align: center;
  opacity: 0.7;
  -webkit-box-align: center;
  -ms-flex-align: center;
  align-items: center;
  display: inline-flex;
  height: 70px;
  font-size: 70px;
  -webkit-box-pack: center;
  font-weight: 400;
  font-family: "Calibre", sans-serif !important;
}

.time-segment:hover {
  opacity: 0.9;
}

.time-segment.active {
  opacity: 1;
  color: white;
  font-weight: 400;
}

.time-separator {
  margin: 0 4px;
  user-select: none;
  opacity: 0.8;
  font-size: 70px;
  font-family: "Calibre", sans-serif !important;
}

.ampm-text {
  color: #FFFFFF;
  opacity: 0.6;
  font-weight: 400;
  margin-left: 8px;
  font-size: 16px;
  user-select: none;
  font-family: "Calibre", sans-serif !important;
}

.clock-container {
  padding: 12px;
  position: relative;
}

.clock-face {
  width: 240px;
  height: 240px;
  border-radius: 50%;
  background: #e0e0e0;
  position: relative;
  margin: 0 auto 0px;
}

.clock-numbers-circle {
  position: absolute;
  top: 50%;
  left: 50%;
  width: calc(100% - 40px);
  height: calc(100% - 40px);
  border-radius: 50%;
  transform: translate(-50%, -50%);
  pointer-events: none;
}

.clock-number {
  position: absolute;
  width: 36px;
  height: 36px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: clamp(16px, 2.5vw, 18px);
  font-weight: 500;
  font-family: "Calibre", sans-serif !important;
  letter-spacing: -1%;
  line-height: 1;
  color: rgba(0, 0, 0, 0.87);
  cursor: pointer;
  border-radius: 50%;
  margin: -18px 0 0 -18px;
  transition: all 0.2s ease;
  user-select: none;
}

.clock-number:hover {
  color: #003F2D;
}

.clock-number.active {
  background: #003F2D;
  color: white;
  font-weight: 600;
}

.minute-number {
  font-size: clamp(16px, 2vw, 16px);
  font-weight: 400;
  width: 32px;
  height: 32px;
  margin: -16px 0 0 -16px;
  letter-spacing: -1%;
  font-family: "Calibre", sans-serif !important;
}

.clock-center {
  position: absolute;
  top: 50%;
  left: 50%;
  width: 8px;
  height: 8px;
  background: #003F2D;
  border-radius: 50%;
  transform: translate(-50%, -50%);
  z-index: 3;
}

.hour-hand {
  height: calc(42% - 20px);
  width: 2px;
  bottom: 50%;
  left: 50%;
  -webkit-transform-origin: center bottom;
  transform-origin: center bottom;
  position: absolute;
  will-change: transform;
  background: #003F2D;
  border-radius: 2px;
  z-index: 2;
  transition: all 0.3s ease;
  margin-left: -1px;
}

.hour-hand::before {
  background: transparent;
  border-width: 2px;
  border-style: solid;
  border-color: inherit;
  border-radius: 100%;
  width: 10px;
  height: 10px;
  content: "";
  position: absolute;
  top: -4px;
  left: 50%;
  -webkit-transform: translate(-50%, -50%);
  transform: translate(-50%, -50%);
}

.hour-hand.hide-pointer::before {
  display: none;
}

.minute-hand {
  height: calc(46% - 20px);
  width: 2px;
  bottom: 50%;
  left: 50%;
  -webkit-transform-origin: center bottom;
  transform-origin: center bottom;
  position: absolute;
  will-change: transform;
  background: #003F2D;
  border-radius: 1.5px;
  z-index: 1;
  transition: all 0.3s ease;
  margin-left: -1px;
}

.minute-hand::before {
  background: transparent;
  border-width: 2px;
  border-style: solid;
  border-color: inherit;
  border-radius: 100%;
  width: 10px;
  height: 10px;
  content: "";
  position: absolute;
  top: -4px;
  left: 50%;
  -webkit-transform: translate(-50%, -50%);
  transform: translate(-50%, -50%);
}

.minute-hand.hide-pointer::before {
  display: none;
}

.minute-hand::after {
  content: "";
  position: absolute;
  height: 8px;
  width: 8px;
  top: 100%;
  left: 50%;
  border-radius: 100%;
  border-style: solid;
  border-color: inherit;
  background-color: inherit;
  -webkit-transform: translate(-50%, -50%);
  transform: translate(-50%, -50%);
}

.ampm-selector {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin: 0 0px;
}

.am-btn, .pm-btn {
  background: transparent;
  border: none;
  padding: 8px 16px;
  opacity: 0.6;
  cursor: pointer;
  font-size: 14px;
  font-weight: 400;
  color: #003f2d !important;
  border-radius: 20px;
  transition: all 0.2s ease;
  min-width: 48px;
  font-family: "Calibre", sans-serif !important;
}


.am-btn.active, .pm-btn.active {
  color: #003F2D;
  opacity: 1;
}

.am-btn:hover, .pm-btn:hover {
  color: #003F2D;
  opacity: 1;
}

.action-buttons {
  display: flex;
  justify-content: flex-end;
  align-items: center;
  padding: 8px 16px;
  gap: 8px;
}

.action-buttons :deep(.v-btn) {
  text-transform: none;
  font-weight: 500;
  min-width: 64px;
  font-family: "Calibre", sans-serif !important;
}

.error-message {
  color: #f44336;
  font-size: 12px;
  margin-top: 4px;
  margin-bottom: 0;
  font-family: "Calibre", sans-serif !important;
  line-height: 1.2;
  min-height: 16px; /* Consistent height for error text */
}

/* Responsive design */
@media (max-width: 400px) {
  .circular-time-picker {
    margin: 0 8px;
  }
  
  .clock-face {
    width: 220px;
    height: 220px;
  }
  
  .hour-hand {
    height: calc(42% - 15px);
  }
  
  .minute-hand {
    height: calc(46% - 15px);
  }
  
  .time-header {
    padding: 16px;
  }
  
  .time-display {
    font-size: 32px;
  }
  
  .ampm-text {
    font-size: 14px;
    margin-left: 6px;
    font-family: "Calibre", sans-serif !important;
  }
  
  .clock-container {
    padding-left: 10px;
  }
  
  .ampm-selector {
    margin: 0 0px;
  }
  .time-picker-component {
    width: 100%;
  }
}

@media (max-width: 320px) {
  .clock-face {
    width: 200px;
    height: 200px;
  }
  
  .clock-number {    
    font-family: "Calibre", sans-serif !important;
    color: rgba(0, 0, 0, 0.87);
    font-weight: 500;
    font-size: clamp(12px, 2vw, 14px);
    letter-spacing: -1%;
    width: 32px;
    height: 32px;
    margin: -16px 0 0 -16px;
  }
  
  .minute-number {
    font-size: clamp(12px, 1.8vw, 12px);
    font-weight: 400;
    width: 28px;
    height: 28px;
    margin: -14px 0 0 -14px;
    letter-spacing: -1%;
    font-family: "Calibre", sans-serif !important;
  }
  
  .hour-hand {
    height: calc(46% - 12px);
  }
  
  .minute-hand {
    height: calc(46% - 12px);
  }
  
  .time-display {
    font-size: 28px;
    font-family: "Calibre", sans-serif !important;
  }
  
  .ampm-text {
    font-size: 12px;
    margin-left: 4px;
    font-family: "Calibre", sans-serif !important;
  }

  .time-picker-component {
    width: 100%;
  }

  .am-btn, .pm-btn {
    font-size:12px;
    font-family: "Calibre", sans-serif !important;
  }
}
</style>
